import { Mail } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { authApi } from '~/entities/session/api/auth'
import { useAuth } from '~/entities/session/model/use-auth'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/ui/dialog'

interface DeleteAccountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteAccountDialog({ open, onOpenChange }: DeleteAccountDialogProps) {
  const { user } = useAuth()
  const [sent, setSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleRequestDeletion = async () => {
    if (!user?.email) {
      toast.error(m.pleaseEnterYourEmail())
      return
    }

    setIsLoading(true)
    try {
      await authApi.requestAccountDeletion(user.email)
      setSent(true)
    } catch (error) {
      console.error('Failed to request account deletion:', error)
      toast.error(getErrorMessageFromError(error))
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setSent(false)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        {sent ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                {m.checkYourEmail()}
              </DialogTitle>
              <DialogDescription>
                {m.accountDeletionEmailSent({ email: user?.email ?? '' })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={handleClose}>{m.close()}</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-destructive">{m.deleteAccount()}</DialogTitle>
              <DialogDescription>{m.deleteAccountEmailInstructions()}</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                <p className="text-sm text-destructive">{m.deleteAccountWarning()}</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={isLoading}>
                {m.cancel()}
              </Button>
              <Button variant="destructive" onClick={handleRequestDeletion} disabled={isLoading}>
                {isLoading ? m.sending() : m.sendDeletionEmail()}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
